import { colors } from '@kmns-internal/furikake'
import {
  Button,
  Chip,
  Divider,
  Drawer,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import { useFormik } from 'formik'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Yup from 'src/features/validation/yup'
import { updateTemplateNode } from 'src/state/ducks/templates/actions'
import { closeEditDeviateComment } from 'src/state/ducks/templates/editDeviateComment/actions'
import type { RootState } from 'src/state/store'
import { DEFAULT_DEVIATE_COMMENT } from '../common/types'

const validationSchema = Yup.object().shape({
  deviateComment: Yup.string().required().label('違反時コメント'),
})

const EditDeviateCommentDrawer: React.FC = () => {
  const dispatch = useDispatch()

  const { questionNode, isOpenModal: isOpen } = useSelector(
    (state: RootState) => state.templatesState.editDeviateComment,
  )

  const initialValues = {
    deviateComment:
      questionNode?.question?.deviateComment === ''
        ? DEFAULT_DEVIATE_COMMENT
        : questionNode?.question?.deviateComment,
  }

  const saveDeviateComment = (
    node: TemplateNodeSchema,
    deviateComment: string,
  ) => {
    dispatch(
      updateTemplateNode(node.id, {
        ...node,
        question: { ...node.question, deviateComment: deviateComment },
      }),
    )
    dispatch(closeEditDeviateComment())
  }

  const { values, handleSubmit, handleChange, resetForm, errors, touched } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: (values, formikHelpers) => {
        // 単に型の都合であり、実際にはundefinedになることはない想定
        if (questionNode === undefined || values.deviateComment === undefined) {
          return
        }
        saveDeviateComment(questionNode, values.deviateComment)
        formikHelpers.resetForm()
      },
      enableReinitialize: true,
    })

  const handleClose = () => {
    resetForm()
    dispatch(closeEditDeviateComment())
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        handleClose()
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '100%',
            width: '100%',
            maxWidth: 600,
          },
        },
      }}
    >
      <Stack sx={{ minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ my: 2, px: 3 }}>
          逸脱時警告メッセージの編集
        </Typography>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <Stack spacing={1} direction="row" alignItems="center">
            <Typography fontWeight="bold">
              「{questionNode?.question?.name}」の逸脱時警告メッセージ
            </Typography>
            <Chip label="必須" variant="required" size="small" />
          </Stack>
          <TextField
            name="deviateComment"
            multiline
            rows={4}
            value={values.deviateComment}
            onChange={handleChange}
            error={touched.deviateComment && Boolean(errors.deviateComment)}
            slotProps={{
              htmlInput: { 'aria-label': '逸脱時警告メッセージ' },
            }}
          />
          {touched.deviateComment && errors.deviateComment && (
            <Typography variant="body1" color={colors.error.main}>
              {errors.deviateComment}
            </Typography>
          )}
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" onClick={handleClose}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={() => handleSubmit()}>
              保存
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default EditDeviateCommentDrawer
